/**
 * Sub-Agent Parallel Visualization Component
 *
 * Renders a Cursor/Grok-inspired real-time view of multiple specialists
 * executing concurrently. Each specialist appears as an animated lane
 * that transitions through queued → running → completed/failed.
 *
 * Uses Framer Motion for 60fps orchestrated animations aligned with
 * FlowState design philosophy (ADHD-friendly, progressive disclosure,
 * reduced cognitive load).
 */

import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Brain,
  Search,
  Calendar,
  BarChart3,
  TrendingUp,
  Users,
  FileText,
  Dumbbell,
  CheckCircle2,
  XCircle,
  Loader2,
  GitBranch,
  Zap,
} from 'lucide-react';
import { cn } from '@flowstate/utils';
import type { SubAgentActivity, SubAgentInfo } from '../types/chat';

interface SubAgentVisualizationProps {
  activity: SubAgentActivity;
}

/** Maps specialist type prefixes to icons for visual identification */
const SPECIALIST_ICONS: Record<string, React.ReactNode> = {
  'task': <Brain className="h-3.5 w-3.5" />,
  'web-search': <Search className="h-3.5 w-3.5" />,
  'research': <Search className="h-3.5 w-3.5" />,
  'schedule': <Calendar className="h-3.5 w-3.5" />,
  'report': <BarChart3 className="h-3.5 w-3.5" />,
  'analytics': <BarChart3 className="h-3.5 w-3.5" />,
  'sales': <TrendingUp className="h-3.5 w-3.5" />,
  'pipeline': <TrendingUp className="h-3.5 w-3.5" />,
  'client': <Users className="h-3.5 w-3.5" />,
  'crm': <Users className="h-3.5 w-3.5" />,
  'content': <FileText className="h-3.5 w-3.5" />,
  'accounting': <FileText className="h-3.5 w-3.5" />,
  'fitness': <Dumbbell className="h-3.5 w-3.5" />,
  'nutrition': <Dumbbell className="h-3.5 w-3.5" />,
  'workout': <Dumbbell className="h-3.5 w-3.5" />,
};

/** Resolve an icon for a specialist type by matching known prefixes */
function getSpecialistIcon(specialistType: string): React.ReactNode {
  for (const [prefix, icon] of Object.entries(SPECIALIST_ICONS)) {
    if (specialistType.startsWith(prefix) || specialistType.includes(prefix)) {
      return icon;
    }
  }
  return <Zap className="h-3.5 w-3.5" />;
}

/** Status-specific styling and icon */
function StatusIndicator({ status }: { status: SubAgentInfo['status'] }) {
  switch (status) {
    case 'running':
      return (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
        >
          <Loader2 className="h-3.5 w-3.5 text-primary" />
        </motion.div>
      );
    case 'completed':
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 15 }}
        >
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
        </motion.div>
      );
    case 'failed':
      return (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 15 }}
        >
          <XCircle className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
        </motion.div>
      );
    case 'queued':
    default:
      return <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30" />;
  }
}

/** Single sub-agent lane */
const SubAgentLane: React.FC<{ agent: SubAgentInfo; index: number }> = ({ agent, index }) => {
  const icon = useMemo(() => getSpecialistIcon(agent.specialistType), [agent.specialistType]);

  return (
    <motion.div
      initial={{ opacity: 0, x: -16, height: 0 }}
      animate={{ opacity: 1, x: 0, height: 'auto' }}
      exit={{ opacity: 0, x: 16, height: 0 }}
      transition={{
        duration: 0.3,
        delay: index * 0.08,
        height: { duration: 0.25 },
      }}
      className="overflow-hidden"
    >
      <div
        className={cn(
          'relative flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-300',
          agent.status === 'running' && 'bg-primary/8 dark:bg-primary/12',
          agent.status === 'completed' && 'bg-green-500/8 dark:bg-green-400/12',
          agent.status === 'failed' && 'bg-red-500/8 dark:bg-red-400/12',
          agent.status === 'queued' && 'bg-muted/50',
        )}
      >
        {/* Note: `relative` on this div ensures the absolute progress bar at bottom is scoped to this lane */}
        {/* Specialist icon */}
        <div
          className={cn(
            'flex-shrink-0 p-1 rounded-md',
            agent.status === 'running' && 'text-primary',
            agent.status === 'completed' && 'text-green-600 dark:text-green-400',
            agent.status === 'failed' && 'text-red-600 dark:text-red-400',
            agent.status === 'queued' && 'text-muted-foreground',
          )}
        >
          {icon}
        </div>

        {/* Name & optional duration */}
        <div className="flex-1 min-w-0">
          <span
            className={cn(
              'text-xs font-medium truncate block',
              agent.status === 'running' && 'text-primary',
              agent.status === 'completed' && 'text-green-700 dark:text-green-300',
              agent.status === 'failed' && 'text-red-700 dark:text-red-300',
              agent.status === 'queued' && 'text-muted-foreground',
            )}
          >
            {agent.displayName}
          </span>
          {agent.durationMs != null && agent.status !== 'running' && (
            <span className="text-[10px] text-muted-foreground">
              {agent.durationMs < 1000
                ? `${agent.durationMs}ms`
                : `${(agent.durationMs / 1000).toFixed(1)}s`}
            </span>
          )}
        </div>

        {/* Status indicator */}
        <StatusIndicator status={agent.status} />

        {/* Running progress bar per lane */}
        {agent.status === 'running' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 overflow-hidden rounded-full">
            <motion.div
              className="h-full bg-primary/40"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

/**
 * Main sub-agent visualization container.
 * Renders when the brain dispatches ≥ 2 parallel specialists.
 */
export const SubAgentVisualization: React.FC<SubAgentVisualizationProps> = ({ activity }) => {
  const { agents, isActive } = activity;

  if (!agents || agents.length === 0) { return null; }

  const completedCount = agents.filter(a => a.status === 'completed').length;
  const failedCount = agents.filter(a => a.status === 'failed').length;
  const allDone = !isActive && (completedCount + failedCount) === agents.length;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ duration: 0.25 }}
      className="w-full"
    >
      <div className="rounded-xl border border-primary/15 bg-gradient-to-br from-primary/[0.03] via-transparent to-accent/[0.03] overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/10">
          <GitBranch className="h-3.5 w-3.5 text-primary/70" />
          <span className="text-xs font-semibold text-primary/80">
            {isActive
              ? `Running ${agents.length} sub-agents in parallel`
              : allDone
                ? `${completedCount}/${agents.length} sub-agents completed`
                : `Sub-agents dispatched`}
          </span>

          {/* Aggregate running indicator */}
          {isActive && (
            <motion.div
              className="ml-auto flex space-x-0.5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  className="w-1 h-1 rounded-full bg-primary"
                  animate={{ opacity: [1, 0.25, 1] }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                />
              ))}
            </motion.div>
          )}

          {allDone && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="ml-auto text-[10px] text-green-600 dark:text-green-400 font-medium"
            >
              ✓ done
            </motion.span>
          )}
        </div>

        {/* Agent lanes */}
        <div className="p-1.5 space-y-1">
          <AnimatePresence mode="sync">
            {agents.map((agent, idx) => (
              <SubAgentLane key={agent.id} agent={agent} index={idx} />
            ))}
          </AnimatePresence>
        </div>

        {/* Aggregate progress bar */}
        {isActive && (
          <div className="relative h-0.5 bg-muted overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-primary/50 via-accent/50 to-primary/50"
              animate={{ x: ['-100%', '100%'] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SubAgentVisualization;
